/**
 * useBlockedSenders — tells the mail list which of the senders it is currently
 * showing are blocked, so their rows get the small x (mirroring the green lead
 * check).
 *
 * Blocking a contact from the inbox (see useBlockContact) writes a canonical
 * entry into `World/{uid}/blacklist`. Two shapes exist:
 *   - EMAIL-level block  → doc id / `email` is the full address (jane@acme.com)
 *   - DOMAIN-level block → doc id / `email` is the domain WITH a leading "@"
 *     (e.g. "@acme.com"); every sender on that domain is blocked.
 *
 * This used to be a live `onSnapshot` over the entire blacklist collection. On
 * a world with a bulk imported blacklist that is ~28.000 documents / 18 MB
 * downloaded on every inbox open, purely to decide up to 50 icons — and since
 * it is bandwidth bound it made the very same world feel fast or unusable
 * depending only on the member's connection.
 *
 * Now we ask the `checkBlacklistBatch` callable about the addresses actually on
 * screen and cache the answers (see utils/blockedSenderIndex). Cost scales with
 * the visible senders instead of the blacklist, and scrolling is nearly free
 * because resolved addresses — and whole resolved domains — are never asked
 * about twice. This is the same lookup the CSV importer, Web Research and the
 * DM Email Finder already use.
 *
 * Trade-off, identical to the one the lead index already makes: a blacklist
 * change made elsewhere (Blacklist settings, another device) is picked up on
 * the next inbox open rather than instantly. Blocks performed here stay
 * instant — `markBlockedLocally` records them without a round trip.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { lookupBlacklistForEmails } from '../../../shared/blacklistUtils';
import {
  emptyIndex,
  senderAddress,
  collectSenderAddresses,
  pendingAddresses,
  mergeLookupResult,
  markBlocked,
  isAddressBlocked,
} from '../utils/blockedSenderIndex';

/**
 * @param {string} userId  active world id
 * @param {Array}  mails   the mails currently rendered; only their senders are
 *                         resolved. Safe to pass a new array every render — the
 *                         effect keys off the addresses, not the array identity.
 */
export default function useBlockedSenders(userId, mails) {
  const [index, setIndex] = useState(emptyIndex);

  // The effect reads the newest index without re-binding on every merge, which
  // would otherwise re-run the lookup for the addresses it just resolved.
  const indexRef = useRef(index);
  const applyIndex = useCallback((next) => {
    indexRef.current = next;
    setIndex(next);
  }, []);

  // Addresses already being looked up, so a re-render mid-flight can't queue
  // them a second time.
  const inFlightRef = useRef(new Set());

  // Only the address list matters; join it so a re-render with the same senders
  // does not restart the effect.
  const addresses = useMemo(() => collectSenderAddresses(mails), [mails]);
  const addressKey = addresses.join(',');

  // A different world has a different blacklist — drop everything we knew.
  useEffect(() => {
    inFlightRef.current = new Set();
    applyIndex(emptyIndex());
  }, [userId, applyIndex]);

  useEffect(() => {
    if (!userId) return undefined;

    const pending = pendingAddresses(addresses, indexRef.current)
      .filter((address) => !inFlightRef.current.has(address));
    if (pending.length === 0) return undefined;

    pending.forEach((address) => inFlightRef.current.add(address));
    let cancelled = false;

    lookupBlacklistForEmails(pending, userId)
      .then((result) => {
        if (cancelled) return;
        applyIndex(mergeLookupResult(indexRef.current, pending, result));
      })
      .catch((err) => {
        // Best effort: without an answer we simply don't draw the x. Never
        // block the list on it.
        console.warn('[useBlockedSenders] blacklist lookup failed:', err?.message);
      })
      .finally(() => {
        pending.forEach((address) => inFlightRef.current.delete(address));
      });

    return () => { cancelled = true; };
    // `addressKey` stands in for `addresses` (same content ⇒ same work).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, addressKey, applyIndex]);

  const isBlockedSender = useCallback(
    (from) => isAddressBlocked(senderAddress(from), index),
    [index],
  );

  /**
   * Records a block we just wrote ourselves, so the x appears immediately.
   * @param {string} blockEntry canonical blacklist doc id ("@acme.com" | address)
   */
  const markBlockedLocally = useCallback((blockEntry) => {
    applyIndex(markBlocked(indexRef.current, blockEntry));
  }, [applyIndex]);

  return useMemo(
    () => ({ isBlockedSender, markBlockedLocally }),
    [isBlockedSender, markBlockedLocally],
  );
}
